// Columns of the tour's sample upload file only. The API's template has more
// columns and is the one users download.
export const BULK_USER_EXCEL_COLUMNS = [
  'userId',
  'name',
  'loginEmail',
  'mobileNumber',
  'country',
  'employeeId',
  'company',
  'entity',
  'payroll',
  'department',
  'designation',
  'jobGrade',
  'costCentre',
  'division',
  'dateJoined',
  'tenderDate',
  'lastDayOfService',
  'rehireDate',
  'keyManagementGrade',
  'moratoriumEffectiveDate',
  'cdpAccountName',
  'cdpAccountNumber',
  'daAccountName',
  'depositoryAgent',
  'depositoryAgentAccountNumber',
  'creditingAccount',
  'terminationReason',
  'userStatus',
  'remarks',
  'additionalField1',
  'additionalField2',
  'additionalField3',
  'additionalField4',
] as const;

export const BULK_USER_EXCEL_HEADERS = [
  'User ID',
  'Name',
  'Login Email',
  'Mobile Number',
  'Country',
  'Employee ID',
  'Company',
  'Entity',
  'Payroll',
  'Department',
  'Designation',
  'Job Grade',
  'Cost Centre',
  'Division',
  'Date Joined',
  'Tender Date',
  'Last Day of Service',
  'Rehire Date',
  'Key Management Grade',
  'Moratorium Effective Date',
  'CDP Account Name',
  'CDP Account Number',
  'DA Account Name',
  'Depository Agent',
  'Depository Agent Account Number',
  'Crediting Account',
  'Termination Reason',
  'User Status',
  'Remarks',
  'Additional Field 1',
  'Additional Field 2',
  'Additional Field 3',
  'Additional Field 4',
] as const;

export const BULK_USER_FIELD_DESCRIPTIONS = [
  'Text | Mandatory: Yes | Unique Identifier',
  'Text | Mandatory: Yes | Full name of user',
  'Email | Mandatory: Yes | Must follow valid email format',
  'Text | Mandatory: No | Personal phone number',
  'Dropdown | Mandatory: No | From Manage Country Parameter',
  'Text | Mandatory: Yes | Unique employee identifier',
  'Dropdown | Mandatory: Yes | From Manage Company Parameter',
  'Dropdown | Mandatory: Yes | From Manage Entity Parameter',
  'Dropdown | Mandatory: Yes | From Manage Payroll Parameter',
  'Dropdown | Mandatory: No | From Manage Department Parameter',
  'Text | Mandatory: No | Role of the user',
  'Dropdown | Mandatory: No | From Manage Job Grade Parameter',
  'Dropdown | Mandatory: No | From Manage Cost Centre Parameter',
  'Dropdown | Mandatory: No | From Manage Division Parameter',
  'Date (dd/mm/yyyy) | Mandatory: No | Start date',
  'Date | Mandatory: No | Tender date if applicable',
  'Date | Mandatory: No | Last day of service if applicable',
  'Date | Mandatory: No | Rehire date if applicable',
  'Dropdown | Mandatory: No | From Manage Key Management Grade Parameter',
  'Date | Mandatory: Yes if Key Management is true',
  'Text | Mandatory: No | Name as per CDP',
  'Text | Mandatory: No (Yes if CDP Account Name entered) | Format: XXXX-XXXX-XXXX',
  'Text | Mandatory: No | Name as per DA account',
  'Dropdown | Mandatory: No | From Manage Depository Agent',
  'Text | Mandatory: No (Yes if DA Account Name entered) | Format: XXXX-XXXX-XXXX',
  'Radio Button | Mandatory: Yes | CDP or DA (Default CDP)',
  'Dropdown | Mandatory: No (Yes if Termination Date entered) | Reason if terminated',
  'Mandatory: Yes | Active/Inactive/Disabled',
  'Text | Mandatory: No | Additional notes',
  '',
  '',
  '',
  '',
] as const;

// User Bulk Upload API (/AwardManagement/userupload) models

/** File status the API derives from the file's workflow action. */
export type UserUploadFileStatus =
  | 'Draft'
  | 'Pending Approval'
  | 'Approved'
  | 'Rejected'
  | 'Pending Revision';

/** Row validation status: `Success` is ready for upload, `Error` failed. */
export type UserUploadRowStatus = 'Success' | 'Error';

export interface UserUploadResult {
  fileId: number;
  fileName: string;
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  globalErrors: string[];
}

export interface UserUploadFile {
  fileId: number;
  fileName: string;
  totalRecords: number;
  readyForUploadCount: number;
  exceptionCount: number;
  excludedCount: number;
  createdUserCount: number;
  status: UserUploadFileStatus;
  workFlowActionId: number;
  workflowActionName: string | null;
  createdById: number;
  uploadedBy: string;
  uploadedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  makerRemarks: string | null;
  checkerRemarks: string | null;
}

export interface UserUploadDetail {
  id: number;
  status: UserUploadRowStatus;
  isExcluded: boolean;
  errorMessages: string[] | null;
  createdUserId: number | null;
  userId?: string | null;
  fullName?: string | null;
  email?: string | null;
  mobileNumber?: string | null;
  country?: string | null;
  employeeId?: string | null;
  company?: string | null;
  entity?: string | null;
  payroll?: string | null;
  department?: string | null;
  designation?: string | null;
  jobGrade?: string | null;
  costCentre?: string | null;
  division?: string | null;
  dateJoined?: string | null;
  tenderDate?: string | null;
  lastWorkingDate?: string | null;
  rehireDate?: string | null;
  keyManagementGrade?: string | null;
  moratoriumEffectiveDate?: string | null;
  cdpAccountName?: string | null;
  cdpAccountNumber?: string | null;
  daAccountName?: string | null;
  depositoryAgent?: string | null;
  depositoryAgentAccountNumber?: string | null;
  creditingAccountType?: string | null;
  terminationReasonCode?: string | null;
  terminationReason?: string | null;
  userRole?: string | null;
  userGroup?: string | null;
  userStatus?: string | null;
  remarks?: string | null;
  additionalField1?: string | null;
  additionalField2?: string | null;
  additionalField3?: string | null;
  additionalField4?: string | null;
}

export interface UserUploadPagedResult<T> {
  items: T[];
  totalCount: number;
  offset: number;
  limit: number;
}

export interface UserUploadDetailsResult extends UserUploadPagedResult<UserUploadDetail> {
  readyForUploadCount: number;
  exceptionCount: number;
  excludedCount: number;
  fileName: string;
  status: UserUploadFileStatus;
  workflowActionName: string | null;
  createdBy: number;
  makerName: string | null;
  uploadedAt: string | null;
  updatedBy: number | null;
  approvedBy: number | null;
  /** The approver; a checker who disapproved or sent it back is not named. */
  checkerName: string | null;
  approvedAt: string | null;
  makerRemarks: string | null;
  checkerRemarks: string | null;
}

export interface UserUploadCounts {
  totalReadyForUpload: number;
  totalExceptions: number;
  totalExcluded: number;
  stillNotExcludedCount: number;
}
